<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';

import { IconEdit, IconEye, IconEyeOff, IconMenu } from '@/components/design/icons';
import type { ContractManagementViewInDb } from '@/api';
import type {
  StaticColumnType,
  StaticColumnInfo,
  ViewColumnOrderItem,
} from '@/types/contractManagement/viewTypes';
import { gaiaAccent, gaiaNeutral } from '@/components/design/themes/tokens';

interface Props {
  show: boolean;
  columns: ContractManagementViewInDb['columns'];
  staticColumns?: StaticColumnInfo[];
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  (e: 'toggle-visibility', columnUid: string): void;
  (e: 'toggle-static-visibility', staticColumnType: StaticColumnType): void;
  (e: 'edit', columnUid: string): void;
  (e: 'edit-static', staticColumnType: StaticColumnType): void;
  (e: 'reorder', columns: ViewColumnOrderItem[]): void;
}

const props = withDefaults(defineProps<Props>(), {
  staticColumns: () => [],
});

const emit = defineEmits<Emits>();

const showPopover = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

/** One row of the shown list — a static column or a view column. */
interface ShownColumn {
  key: string;
  name: string;
  orderIndex: number;
  /** The Name column: always first, never hidden, never dragged. */
  locked: boolean;
  /** Set for a static column; `uid` is set instead for a view column. */
  staticType: StaticColumnType | null;
  uid: string | null;
}

/**
 * The shown columns as one sequence. The grid interleaves static and dynamic
 * columns by order_index, so the popover has to list them together — the two
 * separate blocks this replaced showed an order the grid never had once a
 * custom column was dragged above a static one. Ties keep dynamic before
 * static, matching the backend's own tie-break in `table_column_entries`.
 */
const shownColumns = computed<ShownColumn[]>(() => {
  const rows: ShownColumn[] = [];

  for (const col of props.staticColumns) {
    if (!col.visible) continue;
    rows.push({
      key: col.fieldKey,
      name: col.name,
      orderIndex: col.order_index,
      locked: col.type === 'name',
      staticType: col.type,
      uid: null,
    });
  }

  for (const col of props.columns) {
    if (!col.visible) continue;
    rows.push({
      key: col.uid,
      name: col.display_name,
      orderIndex: col.order_index,
      locked: false,
      staticType: null,
      uid: col.uid,
    });
  }

  return rows.sort((a, b) => {
    if (a.orderIndex !== b.orderIndex) return a.orderIndex - b.orderIndex;
    if (a.uid === null && b.uid !== null) return 1;
    if (a.uid !== null && b.uid === null) return -1;
    return 0;
  });
});

// Static columns - hidden
const hiddenStaticColumns = computed(() => {
  return props.staticColumns
    .filter(col => !col.visible)
    .sort((a, b) => a.order_index - b.order_index);
});

// Dynamic columns - hidden
const hiddenFields = computed(() => {
  return props.columns
    .filter(col => !col.visible)
    .sort((a, b) => a.display_name.localeCompare(b.display_name));
});

function handleToggleVisibility(columnUid: string): void {
  emit('toggle-visibility', columnUid);
}

function handleToggleStaticVisibility(staticColumnType: StaticColumnType): void {
  emit('toggle-static-visibility', staticColumnType);
}

function handleEdit(columnUid: string): void {
  emit('edit', columnUid);
}

function handleEditStatic(staticColumnType: StaticColumnType): void {
  emit('edit-static', staticColumnType);
}

/** The shown rows share one edit / eye pair, whichever kind of column they are. */
function handleRowEdit(row: ShownColumn): void {
  if (row.uid !== null) handleEdit(row.uid);
  else if (row.staticType !== null) handleEditStatic(row.staticType);
}

function handleRowToggle(row: ShownColumn): void {
  if (row.uid !== null) handleToggleVisibility(row.uid);
  else if (row.staticType !== null) handleToggleStaticVisibility(row.staticType);
}

// --- Reordering ---
//
// The gesture follows the template field tree: while a row is carried, an
// accent line marks the gap it would drop into, the rows slide into their new
// places, and the one that landed glows for a moment.
//
// The whole list is the drop zone, not the rows. Listening on rows leaves dead
// zones — the gaps between them, the padding under the last one — where a
// release does nothing, which is most of what makes dropping feel fiddly.
// Instead the gap is read from the pointer's height against the movable rows:
// above a row's midpoint means before it, past the last one means after it.

interface DropTarget {
  key: string;
  position: 'before' | 'after';
}

const draggedKey = ref<string | null>(null);
const dropTarget = ref<DropTarget | null>(null);
/** The row that just landed — highlighted briefly so the eye finds it. */
const settledKey = ref<string | null>(null);
let settledTimer: ReturnType<typeof setTimeout> | null = null;
// Set synchronously on dragstart; `draggedKey` only lands on the next task
// (see below), and the drop must not depend on that having happened yet.
let dragSourceKey: string | null = null;

/** Name is pinned to the front of the grid, so it never joins the sequence. */
const movableColumns = computed(() => shownColumns.value.filter(row => !row.locked));

// While a row is carried the list must scroll itself: HTML5 drag fires no
// wheel or pointer events, and browsers do not auto-scroll an inner overflow
// container, so with twenty columns the bottom of the list is simply
// unreachable. Nearing either edge starts a frame loop that scrolls — faster
// the closer the pointer is — and re-reads the gap as the rows move under it.
const EDGE_ZONE = 30;
const MAX_SCROLL_STEP = 14;

let scrollFrame: number | null = null;
let scrollStep = 0;
let scrollList: HTMLElement | null = null;
let pointerY = 0;

function stopAutoScroll(): void {
  if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
  scrollFrame = null;
  scrollStep = 0;
  scrollList = null;
}

function runAutoScroll(): void {
  const list = scrollList;
  if (list === null || scrollStep === 0 || dragSourceKey === null) {
    stopAutoScroll();
    return;
  }
  const before = list.scrollTop;
  list.scrollTop += scrollStep;
  // The rows have moved under a pointer that hasn't: re-read the gap, or the
  // line stays put while the list slides past it.
  if (list.scrollTop !== before) refreshDropTarget(list);
  scrollFrame = requestAnimationFrame(runAutoScroll);
}

/** Scroll speed from how far into the edge zone the pointer has reached. */
function updateAutoScroll(list: HTMLElement, clientY: number): void {
  const rect = list.getBoundingClientRect();
  const fromTop = clientY - rect.top;
  const fromBottom = rect.bottom - clientY;

  let step = 0;
  if (fromTop < EDGE_ZONE) {
    step = -MAX_SCROLL_STEP * (1 - Math.max(fromTop, 0) / EDGE_ZONE);
  } else if (fromBottom < EDGE_ZONE) {
    step = MAX_SCROLL_STEP * (1 - Math.max(fromBottom, 0) / EDGE_ZONE);
  }

  scrollStep = Math.round(step);
  scrollList = list;
  if (scrollStep === 0) {
    stopAutoScroll();
    return;
  }
  scrollFrame ??= requestAnimationFrame(runAutoScroll);
}

function clearDrag(): void {
  stopAutoScroll();
  dragSourceKey = null;
  draggedKey.value = null;
  dropTarget.value = null;
}

function markSettled(key: string): void {
  if (settledTimer) clearTimeout(settledTimer);
  settledKey.value = key;
  settledTimer = setTimeout(() => {
    settledKey.value = null;
    settledTimer = null;
  }, 700);
}

onBeforeUnmount(() => {
  if (settledTimer) clearTimeout(settledTimer);
  stopAutoScroll();
});

function handleDragStart(row: ShownColumn, event: DragEvent): void {
  if (row.locked) {
    event.preventDefault();
    return;
  }
  dragSourceKey = row.key;

  // Firefox starts no drag at all unless the handler puts something on the
  // dataTransfer, the same as the signing step's recipient list.
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', row.key);
  }

  // Writing reactive state inside dragstart re-renders the dragged element
  // mid-gesture and Chromium cancels the drag — defer past the event.
  setTimeout(() => {
    draggedKey.value = row.key;
  }, 0);
}

/**
 * The shown order with the carried row moved into the gap — or null when the
 * gap is where the row already sits, so a pointless drop draws no line.
 */
function reorderedKeys(sourceKey: string, target: DropTarget): string[] | null {
  const current = movableColumns.value.map(row => row.key);
  const ordered = current.filter(key => key !== sourceKey);
  const anchor = ordered.indexOf(target.key);
  if (anchor === -1) return null;
  ordered.splice(target.position === 'before' ? anchor : anchor + 1, 0, sourceKey);
  return ordered.some((key, index) => key !== current[index]) ? ordered : null;
}

/**
 * Which gap the pointer is over, read against the movable rows in display
 * order: before the first whose midpoint is still below the pointer, else
 * after the last. Nothing under the pointer is dead.
 */
function gapUnderPointer(
  container: HTMLElement,
  sourceKey: string,
  clientY: number
): DropTarget | null {
  const elements = new Map<string, HTMLElement>();
  for (const element of container.querySelectorAll<HTMLElement>('[data-column-key]')) {
    if (element.dataset.columnKey) elements.set(element.dataset.columnKey, element);
  }
  const candidates = movableColumns.value.filter(row => row.key !== sourceKey);
  for (const candidate of candidates) {
    const element = elements.get(candidate.key);
    if (!element) continue;
    const rect = element.getBoundingClientRect();
    if (clientY < rect.top + rect.height / 2) return { key: candidate.key, position: 'before' };
  }
  const last = candidates.at(-1);
  return last ? { key: last.key, position: 'after' } : null;
}

/** Re-read the gap under the last known pointer height and draw it. */
function refreshDropTarget(list: HTMLElement): void {
  const sourceKey = dragSourceKey;
  if (sourceKey === null) return;
  const target = gapUnderPointer(list, sourceKey, pointerY);
  // Only draw the line when the drop would actually move the row.
  const next = target && reorderedKeys(sourceKey, target) ? target : null;
  if (dropTarget.value?.key !== next?.key || dropTarget.value?.position !== next?.position) {
    dropTarget.value = next;
  }
}

function handleListDragOver(event: DragEvent): void {
  const list = event.currentTarget;
  if (dragSourceKey === null || !(list instanceof HTMLElement)) return;
  // Accept the drop anywhere in the list, so no pixel of it is dead.
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  pointerY = event.clientY;
  refreshDropTarget(list);
  updateAutoScroll(list, event.clientY);
}

function toOrderItem(row: ShownColumn): ViewColumnOrderItem | null {
  if (row.uid !== null) return { kind: 'dynamic', uid: row.uid };
  // 'name' is the only static without order_index fields on the view, and it
  // is locked, so it never reaches here.
  if (row.staticType !== null && row.staticType !== 'name') {
    return { kind: 'static', type: row.staticType };
  }
  return null;
}

function handleListDrop(event: DragEvent): void {
  event.preventDefault();
  const sourceKey = dragSourceKey;
  const target = dropTarget.value;
  clearDrag();
  if (sourceKey === null || target === null) return;
  const ordered = reorderedKeys(sourceKey, target);
  if (!ordered) return;

  const byKey = new Map(movableColumns.value.map(row => [row.key, row]));
  const items: ViewColumnOrderItem[] = [];
  for (const key of ordered) {
    const row = byKey.get(key);
    const item = row ? toOrderItem(row) : null;
    if (item) items.push(item);
  }
  if (items.length === 0) return;

  emit('reorder', items);
  markSettled(sourceKey);
}

function dropClass(row: ShownColumn): Record<string, boolean> {
  const target = dropTarget.value;
  const isTarget = target !== null && target.key === row.key && draggedKey.value !== row.key;
  return {
    dragging: draggedKey.value === row.key,
    'drop-before': isTarget && target.position === 'before',
    'drop-after': isTarget && target.position === 'after',
    settled: settledKey.value === row.key,
  };
}

function handleClickOutside(): void {
  clearDrag();
  showPopover.value = false;
}

/**
 * The popover content is teleported to <body>, so `v-bind()` CSS vars — which
 * Vue writes onto this component's own root — never reach it. The drag chrome
 * takes its colours from an inline map on the content root instead.
 */
const styleVars = {
  '--cm-col-drop-line': gaiaAccent.base,
  '--cm-col-settle-tint': gaiaAccent.wash,
  '--cm-col-grip': gaiaNeutral.textMuted,
};
</script>

<template>
  <NPopover
    trigger="manual"
    placement="bottom-start"
    class="column-settings-popover"
    :show="showPopover"
    :show-arrow="false"
    :clickable="true"
    @update:show="showPopover = $event"
    @clickoutside="handleClickOutside"
  >
    <template #trigger>
      <slot name="trigger"></slot>
    </template>

    <!-- Inline custom properties rather than v-bind(): the popover teleports
         to <body>, so component-root CSS vars never reach it. -->
    <div class="columns-popover-content" :style="styleVars">
      <div class="section shown-section">
        <NText strong class="section-title">SHOWN FIELDS</NText>
        <!-- One sequence, static and dynamic together: this is the order the
             grid draws, and dragging a row rewrites it. The list owns the
             dragover/drop so every pixel of it is a valid target, and a
             transition-group so the rows slide once the order comes back. -->
        <transition-group
          v-if="shownColumns.length > 0"
          name="column-row"
          tag="div"
          class="fields-list shown-list"
          @dragover="handleListDragOver"
          @drop="handleListDrop"
        >
          <div
            v-for="row in shownColumns"
            :key="row.key"
            class="field-row"
            :class="dropClass(row)"
            :data-column-key="row.key"
          >
            <div
              class="field-item"
              :draggable="!row.locked || undefined"
              @click.stop
              @dragstart="(event: DragEvent) => handleDragStart(row, event)"
              @dragend="clearDrag"
            >
              <NIcon class="drag-handle" :class="{ locked: row.locked }">
                <IconMenu />
              </NIcon>
              <span class="field-name">{{ row.name }}</span>
              <div class="field-actions">
                <NButton
                  text
                  size="small"
                  class="action-button"
                  :data-tour="row.uid ? 'views.column-edit' : undefined"
                  @click.stop="handleRowEdit(row)"
                >
                  <template #icon>
                    <NIcon><IconEdit /></NIcon>
                  </template>
                </NButton>
                <NButton
                  v-if="!row.locked"
                  text
                  size="small"
                  class="action-button"
                  @click.stop="handleRowToggle(row)"
                >
                  <template #icon>
                    <NIcon><IconEye /></NIcon>
                  </template>
                </NButton>
              </div>
            </div>
          </div>
        </transition-group>
        <div v-else class="empty-state">
          <NText depth="3" style="font-size: 12px">No shown fields</NText>
        </div>
      </div>

      <NDivider style="margin: 12px 0" />

      <div class="section hidden-section">
        <NText strong class="section-title">HIDDEN FIELDS</NText>
        <div
          v-if="hiddenStaticColumns.length > 0 || hiddenFields.length > 0"
          class="fields-list hidden-list"
        >
          <!-- Static Columns (hidden) - With edit option -->
          <div
            v-for="staticCol in hiddenStaticColumns"
            :key="staticCol.fieldKey"
            class="field-item"
            @click.stop
          >
            <NIcon class="drag-handle"><IconMenu /></NIcon>
            <span class="field-name">{{ staticCol.name }}</span>
            <div class="field-actions">
              <NButton
                text
                size="small"
                class="action-button"
                @click.stop="handleEditStatic(staticCol.type)"
              >
                <template #icon>
                  <NIcon><IconEdit /></NIcon>
                </template>
              </NButton>
              <NButton
                text
                size="small"
                class="action-button"
                @click.stop="handleToggleStaticVisibility(staticCol.type)"
              >
                <template #icon>
                  <NIcon><IconEyeOff /></NIcon>
                </template>
              </NButton>
            </div>
          </div>
          <!-- Dynamic Columns (hidden) - With edit option -->
          <div v-for="column in hiddenFields" :key="column.uid" class="field-item" @click.stop>
            <NIcon class="drag-handle"><IconMenu /></NIcon>
            <span class="field-name">{{ column.display_name }}</span>
            <div class="field-actions">
              <NButton
                text
                size="small"
                class="action-button"
                data-tour="views.column-edit"
                @click.stop="handleEdit(column.uid)"
              >
                <template #icon>
                  <NIcon><IconEdit /></NIcon>
                </template>
              </NButton>
              <NButton
                text
                size="small"
                class="action-button"
                @click.stop="handleToggleVisibility(column.uid)"
              >
                <template #icon>
                  <NIcon><IconEyeOff /></NIcon>
                </template>
              </NButton>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">
          <NText depth="3" style="font-size: 12px">No hidden fields</NText>
        </div>
      </div>
    </div>
  </NPopover>
</template>

<style>
.column-settings-popover.n-popover {
  /* This applies the border to the outside of the popover */
  border: 1px solid #d1d5db; /* Grayish border (Tailwind gray-300 equivalent) */
  border-radius: 6px;
  padding: 0; /* Reset default padding so your inner div controls it */
  overflow: hidden;
}
</style>

<style scoped>
/* Renamed to -content to distinguish from the popover container */
.columns-popover-content {
  width: 320px;
  max-height: 560px;
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: white;
  overflow: hidden;
}

.section {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.section:last-child {
  margin-bottom: 0;
}

.section-title {
  display: block;
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.fields-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

/* The shown list is where the dragging happens, so it takes the room the
   hidden list doesn't need — with twenty columns shown and none hidden, that
   is the whole popover rather than a fixed 240px window. */
.shown-section {
  flex: 1 1 auto;
}

.shown-list {
  flex: 1 1 auto;
  min-height: 120px;
  /* Slack under the last row so "move it to the end" has something to aim at. */
  padding-bottom: 8px;
}

.hidden-section {
  flex: 0 1 auto;
}

.hidden-list {
  max-height: 220px;
}

.field-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: default;
  transition: background-color 0.2s;
}

.field-item:hover {
  background-color: #f5f5f5;
}

.drag-handle {
  color: var(--cm-col-grip);
  cursor: grab;
  flex-shrink: 0;
  font-size: 14px;
}

.drag-handle:active {
  cursor: grabbing;
}

/* Name is fixed at the front of the grid, so it keeps the row's indent but
   offers no grip to pull. */
.drag-handle.locked {
  visibility: hidden;
  cursor: default;
}

/* The drag feedback belongs to the list, not the row: an insertion line in the
   gap the drop would fill, a slide when the order changes, a glow on the row
   that landed. Pseudo-elements and keyframes have no component form. */
.field-row {
  position: relative;
  border-radius: 4px;
}

.field-row.dragging {
  opacity: 0.45;
}

.field-row.drop-before::before,
.field-row.drop-after::after {
  content: '';
  position: absolute;
  left: 4px;
  right: 4px;
  height: 2px;
  border-radius: 1px;
  background: var(--cm-col-drop-line);
  pointer-events: none;
}

.field-row.drop-before::before {
  top: -2px;
}

.field-row.drop-after::after {
  bottom: -2px;
}

.column-row-move {
  transition: transform 0.22s cubic-bezier(0.2, 0, 0, 1);
}

.field-row.settled {
  animation: column-row-settle 0.7s ease-out;
}

@keyframes column-row-settle {
  from {
    background: var(--cm-col-settle-tint);
  }
  to {
    background: transparent;
  }
}

.field-name {
  flex: 1;
  font-size: 13px;
  color: #1d2129;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.2s;
}

.field-item:hover .field-actions {
  opacity: 1;
}

.action-button {
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.action-button:hover {
  color: #2080f0;
  background-color: #e6f4ff;
}

.empty-state {
  padding: 8px;
  text-align: center;
}

/* Scrollbar styling */
.fields-list::-webkit-scrollbar {
  width: 6px;
}

.fields-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.fields-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.fields-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
